import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

// Same localStorage key/shape components/Main.jsx has always used (a JSON-encoded
// boolean), so existing users don't lose their preference when this moves here.
const STORAGE_KEY = 'darkMode';

function loadDarkModeFromLocalStorage(): boolean | undefined {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : undefined;
    } catch {
        return undefined;
    }
}

function saveDarkModeToLocalStorage(darkMode: boolean): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(darkMode));
    } catch {
        // Storage disabled (private browsing, quota, etc.) -- darkMode still works
        // for the session via React state, it just won't persist.
    }
}

function getPrefersDarkMode(): boolean {
    return (
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );
}

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
    darkMode: boolean;
    /** shadcn/next-themes-shaped alias of darkMode, for components (e.g. ui/sonner.tsx)
     *  that expect a "light" | "dark" theme string rather than a boolean. */
    theme: ThemeMode;
    setDarkMode: (darkMode: boolean) => void;
    toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [darkMode, setDarkModeState] = useState<boolean>(() => {
        const stored = loadDarkModeFromLocalStorage();
        return stored !== undefined ? stored : getPrefersDarkMode();
    });

    // Drives shadcn's `.dark` class strategy (see the `@custom-variant dark` line
    // in index.css) so stock shadcn/Tailwind components follow the same toggle
    // MUI's ThemeProvider already uses.
    useEffect(() => {
        document.documentElement.classList.toggle('dark', darkMode);
    }, [darkMode]);

    const setDarkMode = (next: boolean) => {
        saveDarkModeToLocalStorage(next);
        setDarkModeState(next);
    };

    const value = useMemo<ThemeContextValue>(
        () => ({
            darkMode,
            theme: darkMode ? 'dark' : 'light',
            setDarkMode,
            toggleDarkMode: () => setDarkMode(!darkMode)
        }),
        [darkMode]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within a <ThemeProvider>');
    }
    return ctx;
}

/*
 * MUI palette objects -- unchanged in shape/values from the ones that used to live
 * in components/Main.jsx, and still exported (re-exported) from there so
 * src/App.jsx's `import { lightModePalette } from './components/Main'` keeps
 * working. ~26 `theme.palette.*` call sites (including the custom `result`,
 * `gender`, `accent`, `paypal` keys) read these via MUI's ThemeProvider until
 * those screens migrate to the Tailwind tokens in index.css.
 */
export const darkModePalette = {
    primary: {
        main: '#FF889C',
        light: '#FF9CAA',
        dark: '#F97893'
    },
    accent: {
        main: '#8AD0E7'
    },
    paypal: {
        main: 'rgb(255, 188, 50)'
    },
    result: {
        win: '#5c95b5',
        loss: '#ad4c4c'
    },
    gender: {
        open: '#E13B5F',
        women: '#a0d9fa',
        mixed: '#AE5CF7',
        boys: '#f28e41',
        girls: '#375cf0'
    },
    actions: {
        hover: '#292929'
    }
};

export const lightModePalette = {
    primary: {
        main: '#913AED',
        light: '#AE5CF7',
        dark: '#7B31DB'
    },
    paypal: {
        main: 'rgb(255, 188, 50)'
    },
    accent: {
        main: '#2580B8'
    },
    result: {
        win: '#a0d9fa',
        loss: '#f9a0a0'
    },
    gender: {
        open: '#E13B5F',
        women: '#1B7FDD',
        mixed: '#913AED',
        boys: '#f28e41',
        girls: '#375cf0'
    },
    actions: {
        hover: '#E9E9E9'
    }
};

export const typographyConfig = {
    fontFamily: ['Mulish', 'sans-serif'].join(',')
};
